/* Dice Goblin 2.0 — global behaviour. Vanilla web components, no dependencies. */
(() => {
  'use strict';

  const config = (() => {
    try {
      return JSON.parse(document.getElementById('theme-config')?.textContent || '{}');
    } catch {
      return {};
    }
  })();
  const routes = config.routes || {};
  /* Shopify's t filter HTML-escapes translations, so the words layout/theme.liquid hands over arrive as We&#39;re:
     decoded once here (contract v7, section 17), then plain text (esc() escapes them again for innerHTML). */
  const decode = (s) => String(s ?? '').replace(/&(amp|lt|gt|quot|#39|#x27);/g, (m, e) => ({ amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'", '#x27': "'" })[e]);
  const strings = Object.fromEntries(Object.entries(config.strings || {}).map(([key, value]) => [key, decode(value)]));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const fill = (text, values) => String(text || '').replace(/\[(\w+)\]/g, (_, key) => values[key] ?? '');

  /** Say something to screen readers. While a dialog is open (the cart drawer), only a live region inside it can be
      heard, so the region is picked when the message goes out: after whatever opened the drawer has run. */
  const announce = (message) => {
    if (!message) return;
    window.setTimeout(() => {
      const region = document.querySelector('dialog[open] [data-live-region]') || document.querySelector('body > [data-live-region]');
      if (!region) return;
      region.textContent = '';
      window.setTimeout(() => {
        region.textContent = message;
      }, 50);
    }, 0);
  };

  /** One announcement from two messages (a cart change and the dice deal's news): each then ends as a sentence. */
  const sentences = (...messages) => {
    const said = messages.filter(Boolean).map((m) => String(m).trim());
    if (said.length < 2) return said[0] || '';
    return said.map((m) => (/[.!?]$/.test(m) ? m : `${m}.`)).join(' ');
  };

  /* ---------- dialogs ---------- */
  const openers = new WeakMap();
  const openDialog = (dialog, opener) => {
    if (!dialog || dialog.open) return;
    openers.set(dialog, opener || document.activeElement);
    dialog.showModal();
    document.body.classList.add('has-open-dialog');
  };
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-dialog-open]');
    if (opener) {
      event.preventDefault();
      openDialog(document.getElementById(opener.dataset.dialogOpen), opener);
      return;
    }
    const closer = event.target.closest('[data-dialog-close]');
    if (closer) closer.closest('dialog')?.close();
  });
  document.addEventListener(
    'close',
    (event) => {
      if (!(event.target instanceof HTMLDialogElement)) return;
      if (!document.querySelector('dialog[open]')) document.body.classList.remove('has-open-dialog');
      const opener = openers.get(event.target);
      openers.delete(event.target);
      if (opener && document.contains(opener)) opener.focus();
    },
    true,
  );
  document.addEventListener('click', (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;
    const rect = dialog.getBoundingClientRect();
    const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
    if (!inside) dialog.close();
  });

  /* Desktop scrollbars: an open dialog hides the page's scrollbar (body.has-open-dialog), and base.css pads the page
     by the scrollbar's width so nothing behind it shifts sideways. Measured while the page is unlocked, whenever its
     width changes (a scrollbar appears once a Lair page has drawn, say). Phones' scrollbars take no room: 0. */
  const root = document.documentElement;
  const measureScrollbar = () => {
    if (document.body.classList.contains('has-open-dialog')) return;
    root.style.setProperty('--scrollbar-width', `${Math.max(0, window.innerWidth - root.clientWidth)}px`);
  };
  measureScrollbar();
  window.addEventListener('resize', measureScrollbar, { passive: true });
  if ('ResizeObserver' in window) new ResizeObserver(measureScrollbar).observe(root);

  /* ---------- the sticky header ----------
     The header's section wrapper sticks to the top of the screen (sections/site-header.liquid). --sticky-top (base.css)
     is its height, so the pages' own sticky bars and anchor jumps sit just under it: measured here, and again whenever
     it changes size (a resize, the logo loading, the text getting bigger). Rounded down, so a sticky bar tucks a
     fraction under the header rather than leaving a hairline gap. */
  const headerBox = (() => {
    const header = document.querySelector('[data-site-header]');
    return header ? header.closest('.shopify-section') || header : null;
  })();
  const measureHeader = () => {
    if (!headerBox) return;
    const height = Math.floor(headerBox.getBoundingClientRect().height);
    if (height > 0) root.style.setProperty('--sticky-top', `${height}px`);
  };
  measureHeader();
  window.addEventListener('resize', measureHeader, { passive: true });
  if (headerBox && 'ResizeObserver' in window) new ResizeObserver(measureHeader).observe(headerBox);

  /* ---------- disclosure menus ---------- */
  const disclosures = () => document.querySelectorAll('[data-disclosure]');
  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!details.matches?.('[data-disclosure]') || !details.open) return;
      for (const other of disclosures()) if (other !== details) other.open = false;
    },
    true,
  );
  document.addEventListener('click', (event) => {
    for (const d of disclosures()) if (d.open && !d.contains(event.target)) d.open = false;
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    for (const d of disclosures()) {
      if (d.open) {
        d.open = false;
        d.querySelector('summary')?.focus();
      }
    }
  });

  /* ---------- announcement marquee ---------- */
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-marquee-toggle]');
    if (!toggle) return;
    const bar = toggle.closest('[data-announcement]');
    const paused = bar.toggleAttribute('data-paused');
    toggle.setAttribute('aria-pressed', String(paused));
    toggle.querySelector('[data-icon-pause]').hidden = paused;
    toggle.querySelector('[data-icon-play]').hidden = !paused;
  });

  /* ---------- opening hours (Lair time zone) ---------- */
  const lair = config.lair || {};
  const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const parseHours = (text) => {
    const week = {};
    String(text || '')
      .split(/\r?\n/)
      .forEach((line) => {
        const m = line.trim().match(/^([A-Za-z]{3})[a-z]*\s+(\d{1,2})(?::(\d{2}))?\s*-\s*(\d{1,2})(?::(\d{2}))?/);
        if (!m) return;
        const open = Number(m[2]) * 60 + Number(m[3] || 0);
        let close = Number(m[4]) * 60 + Number(m[5] || 0);
        if (close <= open) close += 1440;
        week[m[1].toLowerCase()] = [open, close];
      });
    return week;
  };
  const clock = (minutes) => {
    const h = Math.floor(minutes / 60) % 24;
    const mi = minutes % 60;
    if (h === 0 && mi === 0) return 'midnight';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}${mi ? `:${String(mi).padStart(2, '0')}` : ''}${h < 12 ? 'am' : 'pm'}`;
  };
  const lairNow = () => {
    try {
      const parts = new Intl.DateTimeFormat('en-NZ', { timeZone: lair.tz || 'Pacific/Auckland', weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date());
      const get = (type) => parts.find((p) => p.type === type)?.value || '';
      return { day: get('weekday').slice(0, 3).toLowerCase(), minutes: (Number(get('hour')) % 24) * 60 + Number(get('minute')) };
    } catch {
      const d = new Date();
      return { day: DAYS[d.getDay()], minutes: d.getHours() * 60 + d.getMinutes() };
    }
  };
  const updateOpenStatus = () => {
    const week = parseHours(lair.hours);
    const now = lairNow();
    for (const row of document.querySelectorAll('[data-hours-list] [data-day]')) {
      row.toggleAttribute('data-today', row.dataset.day === now.day);
    }
    // the footer's one-line visit details (snippets/hours-today.liquid): only today's hours show
    for (const span of document.querySelectorAll('[data-hours-today] [data-day]')) span.hidden = span.dataset.day !== now.day;
    // the header's pill (screens 1360px and wider) and the top of the phone menu (30 Oct 2026) say the same
    const pills = document.querySelectorAll('[data-open-status]');
    if (!pills.length) return;
    const today = week[now.day];
    let state = 'closed';
    let label = strings.closedToday || 'Closed now';
    if (today && now.minutes >= today[0] && now.minutes < today[1]) {
      state = 'open';
      label = fill(strings.openUntil || 'Open until [time]', { time: clock(today[1]) });
    } else if (today && now.minutes < today[0]) {
      label = fill(strings.opensAt || 'Opens at [time]', { time: clock(today[0]) });
    } else {
      const start = DAYS.indexOf(now.day);
      for (let i = 1; i <= 7; i += 1) {
        const key = DAYS[(start + i) % 7];
        if (week[key]) {
          label = fill(strings.opensDay || 'Opens [day] [time]', { day: key.charAt(0).toUpperCase() + key.slice(1), time: clock(week[key][0]) });
          break;
        }
      }
    }
    for (const pill of pills) {
      pill.dataset.state = state;
      const text = pill.querySelector('[data-open-status-text]');
      if (text) text.textContent = label;
    }
  };
  updateOpenStatus();
  window.setInterval(updateOpenStatus, 60000);

  /* ---------- quantity steppers (generic) ---------- */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-qty-step]');
    if (!button) return;
    const wrap = button.closest('[data-qty]');
    const input = wrap?.querySelector('[data-qty-input]');
    if (!input) return;
    const min = Number(input.min || 0);
    const max = input.max ? Number(input.max) : Infinity;
    const next = Math.min(max, Math.max(min, Number(input.value || 0) + Number(button.dataset.qtyStep)));
    if (String(next) === input.value) return;
    input.value = String(next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- dice burst ---------- */
  /* origin: an element, or where one was (a DOMRect), for a button the cart has re-rendered away */
  const diceBurst = (origin) => {
    if (!config.diceBurst || reduceMotion.matches || !origin) return;
    const rect = typeof origin.getBoundingClientRect === 'function' ? origin.getBoundingClientRect() : origin;
    const burst = document.createElement('div');
    burst.className = 'dice-burst';
    burst.style.left = `${rect.left + rect.width / 2}px`;
    burst.style.top = `${rect.top + rect.height / 2}px`;
    const colours = ['var(--c-goblin)', 'var(--c-gold)', 'var(--c-ruby)', 'var(--c-potion)', 'var(--c-mana)'];
    for (let i = 0; i < 12; i += 1) {
      const die = document.createElement('span');
      const angle = (Math.PI * 2 * i) / 12 + Math.random() * 0.4;
      const dist = 40 + Math.random() * 50;
      die.className = 'dice-burst__die';
      die.style.setProperty('--dx', `${Math.cos(angle) * dist}px`);
      die.style.setProperty('--dy', `${Math.sin(angle) * dist - 20}px`);
      die.style.setProperty('--rot', `${Math.round(Math.random() * 540 - 270)}deg`);
      die.style.setProperty('--die', colours[i % colours.length]);
      burst.append(die);
    }
    burst.setAttribute('aria-hidden', 'true');
    // The cart drawer opens straight after this, in the top layer, and would cover the dice. A manual popover shown
    // in a microtask (after the drawer's showModal() in the same task) goes into the top layer above it instead.
    const onTop = typeof burst.showPopover === 'function';
    if (onTop) burst.popover = 'manual';
    document.body.append(burst);
    if (onTop) queueMicrotask(() => burst.isConnected && burst.showPopover());
    window.setTimeout(() => burst.remove(), 900);
  };

  /* ---------- cart ---------- */
  const cartSectionIds = () => {
    const ids = [];
    if (document.querySelector('cart-drawer')) ids.push('cart-drawer');
    const page = document.querySelector('[data-cart-section]');
    if (page) ids.push(page.dataset.cartSection);
    return ids;
  };

  /* The coin bump on the pouch says "it's in your cart": only when the count goes up. With the cart drawer, which
     covers the header, it waits for the drawer to close, so it's seen as the pouch comes back into view. */
  let bumpOnClose = false;
  const bump = () => {
    for (const el of document.querySelectorAll('[data-cart-count]')) {
      el.classList.remove('is-bumped');
      void el.offsetWidth;
      el.classList.add('is-bumped');
    }
  };
  document.addEventListener(
    'close',
    (event) => {
      if (event.target.id !== 'cart-drawer' || !bumpOnClose) return;
      bumpOnClose = false;
      bump();
    },
    true,
  );

  const updateCount = (count) => {
    const before = Number(document.querySelector('[data-cart-count]')?.textContent) || 0;
    for (const el of document.querySelectorAll('[data-cart-count]')) {
      el.textContent = count > 0 ? String(count) : '';
    }
    if (count > before) {
      if (document.querySelector('cart-drawer') && config.cartType === 'drawer') bumpOnClose = true;
      else bump();
    }
    for (const el of document.querySelectorAll('[data-cart-count-label]')) {
      el.textContent = `${count} ${count === 1 ? 'item' : 'items'}`;
    }
  };

  /* The free-shipping bar (snippets/xp-bar.liquid) grows from where it was to where it's got to when something goes
     in: the new fill starts cut back to the old amount and is uncovered (clip-path, so it isn't laid out again). */
  const xpOf = (scope) => parseFloat(scope?.querySelector('.xp-bar__fill')?.style.getPropertyValue('--xp')) || 0;
  const growXp = (scope, from) => {
    const fill = scope?.querySelector('.xp-bar__fill');
    const to = xpOf(scope);
    if (!fill || !(to > from) || reduceMotion.matches || typeof fill.animate !== 'function') return;
    const easing = getComputedStyle(document.documentElement).getPropertyValue('--ease-out').trim() || 'ease-out';
    fill.animate([{ clipPath: `inset(0 ${(100 - (from / to) * 100).toFixed(2)}% 0 0 round 999px)` }, { clipPath: 'inset(0 0 0 0 round 999px)' }], {
      duration: 600,
      delay: 150,
      easing,
      fill: 'backwards',
    });
  };

  /* The dice deal (snippets/dice-deal.liquid): data-free on its cart panel is how many dice Shopify has made free. */
  const dealFree = (scope) => Number(scope?.querySelector('[data-dice-deal]')?.dataset.free) || 0;

  /* Puts the fresh cart in place. When the change made another dice free, the panel pops, and the panel and its news
     ("Your 3rd dice is free. You saved $90.00.") come back for the caller to say with its own message. */
  const applySections = (sections) => {
    if (!sections) return null;
    const parser = new DOMParser();
    let deal = null;
    for (const [id, html] of Object.entries(sections)) {
      if (!html) continue;
      const doc = parser.parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-cart-content]');
      const target =
        id === 'cart-drawer'
          ? document.querySelector('cart-drawer [data-cart-content]')
          : document.querySelector(`[data-cart-section="${CSS.escape(id)}"] [data-cart-content]`);
      const was = xpOf(target);
      const freeBefore = dealFree(target);
      if (fresh && target) {
        target.replaceWith(fresh);
        growXp(fresh, was);
        const panel = fresh.querySelector('[data-dice-deal]');
        if (panel && dealFree(fresh) > freeBefore) {
          panel.classList.add('is-new');
          if (!deal) deal = { panel, news: panel.dataset.news || '' };
        }
      }
      const count = fresh?.querySelector('[data-cart-count-source]')?.textContent;
      if (count != null) updateCount(Number(count));
    }
    return deal;
  };

  const cartRequest = async (url, body) => {
    const sections = cartSectionIds();
    let response;
    if (body instanceof FormData) {
      if (sections.length) {
        body.append('sections', sections.join(','));
        body.append('sections_url', window.location.pathname);
      }
      response = await fetch(url, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body });
    } else {
      response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections, sections_url: window.location.pathname }),
      });
    }
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || strings.addError || 'Could not update your cart.');
    }
    return data;
  };

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('click', (event) => this.onRemove(event));
    }

    open(opener) {
      openDialog(this.dialog, opener);
    }

    async onChange(event) {
      const input = event.target.closest('[data-qty-input]');
      if (input) {
        const line = input.closest('[data-line]');
        await changeLine(line.dataset.key, Number(input.value), this);
      }
      const note = event.target.closest('[data-cart-note]');
      if (note) saveNote(note.value);
    }

    async onRemove(event) {
      const button = event.target.closest('[data-qty-remove]');
      if (!button) return;
      const line = button.closest('[data-line]');
      await changeLine(line.dataset.key, 0, this);
    }
  }

  let cartQueue = Promise.resolve();
  const changeLine = (key, quantity, scope) => {
    cartQueue = cartQueue.then(async () => {
      const content = scope.querySelector('[data-cart-content]');
      const focusedQty = document.activeElement?.matches?.('[data-qty-input]');
      const before = scope.querySelector(`[data-key="${CSS.escape(key)}"]`);
      const title = before?.querySelector('.cart-item__title')?.textContent.trim() || '';
      content?.setAttribute('aria-busy', 'true');
      try {
        const data = await cartRequest(routes.cartChange ? `${routes.cartChange}.js` : '/cart/change.js', { id: key, quantity });
        const deal = applySections(data.sections);
        // A dice that's just gone free gets the dice burst, from the deal's panel (the drawer is already open)
        if (deal) diceBurst(deal.panel);
        const line = scope.querySelector(`[data-key="${CSS.escape(key)}"]`);
        // Every change is announced, with the quantity the cart actually took, and the deal's news when there is some
        const now = Number(line?.querySelector('[data-qty-input]')?.value ?? (line ? quantity : 0));
        const said =
          !line || now === 0
            ? fill(strings.removed || 'Removed [title] from your cart', { title })
            : fill(strings.quantityUpdated || '[title]: [quantity] in your cart', { title, quantity: now });
        announce(sentences(said, deal?.news));
        const target = line ? line.querySelector(focusedQty ? '[data-qty-input]' : '[data-qty-step="1"], [data-qty-remove]') : null;
        (target || scope.querySelector('[data-dialog-close], .cart-page__title, a, button'))?.focus();
      } catch (error) {
        announce(error.message);
      } finally {
        scope.querySelector('[data-cart-content]')?.removeAttribute('aria-busy');
      }
    });
    return cartQueue;
  };

  let noteTimer = null;
  const saveNote = (note) => {
    window.clearTimeout(noteTimer);
    noteTimer = window.setTimeout(() => {
      fetch(`${routes.cartUpdate || '/cart/update'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note }),
      });
    }, 400);
  };

  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-cart-open]');
    const drawer = document.querySelector('cart-drawer');
    if (!link || !drawer || config.cartType !== 'drawer') return;
    event.preventDefault();
    drawer.open(link);
  });

  /* cart page uses the same handlers */
  const cartPage = document.querySelector('[data-cart-section]');
  if (cartPage) {
    cartPage.addEventListener('change', async (event) => {
      const input = event.target.closest('[data-qty-input]');
      if (input) await changeLine(input.closest('[data-line]').dataset.key, Number(input.value), cartPage);
      const note = event.target.closest('[data-cart-note]');
      if (note) saveNote(note.value);
    });
    cartPage.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-qty-remove]');
      if (button) await changeLine(button.closest('[data-line]').dataset.key, 0, cartPage);
    });
  }

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      // A terms tick (the library join): the form checks it itself and shows Gobgob's own message, whatever the cart
      // type, and still won't add anything until it's ticked. Without JavaScript the browser's required check stays.
      this.terms = this.form.querySelector('[data-terms]');
      if (this.terms) {
        this.form.noValidate = true;
        this.form.addEventListener('submit', (event) => this.checkTerms(event));
        this.terms.addEventListener('change', () => {
          if (!this.terms.checked) return;
          this.terms.removeAttribute('aria-invalid');
          this.showError('');
        });
      }
      if (config.cartType !== 'drawer') return;
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }

    showError(message) {
      const error = this.querySelector('[data-form-error]');
      if (!error) return false;
      error.textContent = message;
      error.hidden = !message;
      return true;
    }

    checkTerms(event) {
      const stop = () => {
        event.preventDefault();
        event.stopImmediatePropagation();
      };
      if (!this.terms.checked) {
        stop();
        this.terms.setAttribute('aria-invalid', 'true');
        this.showError(this.terms.dataset.termsMessage || 'Tick the box to agree to the terms first.');
        this.terms.focus();
        return;
      }
      // Anything else the form needs (a plan): the browser's own check, as before
      if (!this.form.checkValidity()) {
        stop();
        this.form.reportValidity();
      }
    }

    async onSubmit(event) {
      event.preventDefault();
      // The form's own button, or one tied to it from outside (the sticky bar on phones): both show busy
      const own = this.form.querySelector('[type="submit"]');
      const button = event.submitter && event.submitter.form === this.form ? event.submitter : own;
      const buttons = Array.from(this.form.elements).filter((el) => el.type === 'submit');
      if (buttons.some((b) => b.getAttribute('aria-busy') === 'true')) return;
      buttons.forEach((b) => b.setAttribute('aria-busy', 'true'));
      this.showError('');
      // Pick your third (data-stay) sits in a cart that's also taking quantity changes: its add waits its turn in the
      // same queue and holds the cart busy, so the two replies can't paint out of order (code review, 31 Oct 2026)
      const content = this.hasAttribute('data-stay') ? this.closest('[data-cart-content]') : null;
      let done = () => {};
      if (content) {
        const turn = cartQueue;
        const mine = new Promise((resolve) => {
          done = resolve;
        });
        cartQueue = turn.then(() => mine);
        await turn;
        content.setAttribute('aria-busy', 'true');
      }
      try {
        const data = await cartRequest(`${routes.cartAdd || '/cart/add'}.js`, new FormData(this.form));
        // A Pick your third button inside the cart (snippets/dice-deal.liquid) is re-rendered away by the next line:
        // where it was, and which cart it was in, are kept first
        const from = button?.getBoundingClientRect();
        const home = button?.closest('cart-drawer, [data-cart-section]');
        const deal = applySections(data.sections);
        diceBurst(from);
        // round 16: it's in the cart (Order as a gift closes and empties for the next one)
        this.dispatchEvent(new CustomEvent('product-form:added', { bubbles: true }));
        const drawer = document.querySelector('cart-drawer');
        // data-stay: Pick your third, added from a cart that has just updated itself, so no drawer opens over it
        if (drawer && !this.hasAttribute('data-stay')) drawer.open(button);
        const added = fill(strings.added || 'Added to your cart', { title: data.product_title || data.title || '' });
        announce(sentences(added, deal?.news));
        // Focus doesn't go to the page with the button gone: the deal's panel says what happened
        if (home && button && !button.isConnected) {
          (home.querySelector('[data-dice-deal]') || home.querySelector('[data-dialog-close], .cart-page__title'))?.focus();
        }
        // No drawer: off to the cart page, unless this is the cart page (data-stay), which has just updated in place
        if (!drawer && !this.hasAttribute('data-stay')) window.location.href = routes.cart || '/cart';
      } catch (err) {
        if (!this.showError(err.message)) {
          announce(err.message);
          window.location.href = routes.cart || '/cart';
        }
      } finally {
        buttons.forEach((b) => b.removeAttribute('aria-busy'));
        content?.removeAttribute('aria-busy');
        done();
      }
    }
  }

  /* ---------- sticky Add to cart on phones (sections/main-product.liquid) ----------
     Shows while the page's own Add to cart is off screen, below 990px (wider, the buy column sticks beside the
     pictures). Its button submits the same form (form="…"), so ProductForm adds it and opens the cart drawer. */
  const narrow = window.matchMedia('(max-width: 989px)');
  class BuyBar extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-buy-bar-button]');
      const form = this.button ? document.getElementById(this.button.getAttribute('form')) : null;
      this.target = form?.querySelector('[data-add-button]');
      if (!this.target || !('IntersectionObserver' in window)) return;
      this.inView = true;
      this.watch = new IntersectionObserver((entries) => {
        this.inView = entries[entries.length - 1].isIntersecting;
        this.sync();
      });
      this.watch.observe(this.target);
      this.onWidth = () => this.sync();
      narrow.addEventListener?.('change', this.onWidth);
      this.hidden = false;
    }

    disconnectedCallback() {
      this.watch?.disconnect();
      narrow.removeEventListener?.('change', this.onWidth);
    }

    sync() {
      this.toggleAttribute('data-shown', narrow.matches && !this.inView);
    }
  }

  /** Copy the product's price, and its button's label and sold-out state, onto the sticky bar */
  const syncBuyBar = (section) => {
    const bar = section?.querySelector('buy-bar');
    if (!bar) return;
    const price = section.querySelector('[data-product-price] .price');
    const slot = bar.querySelector('[data-buy-bar-price]');
    if (price && slot) {
      slot.innerHTML = price.outerHTML;
      slot.firstElementChild?.classList.remove('price--large');
      slot.firstElementChild?.removeAttribute('data-price');
    }
    const add = section.querySelector('[data-add-button]');
    const button = bar.querySelector('[data-buy-bar-button]');
    if (add && button) {
      button.disabled = add.disabled;
      const label = button.querySelector('[data-buy-bar-label]');
      const from = add.querySelector('[data-add-label]');
      if (label && from) label.textContent = from.textContent.trim();
    }
  };

  /** Round 16, a game page's stock and delivery box (sections/main-product.liquid): the picked variant's "In stock" or
      "Only N left in the hoard" and whether "Ships within N working days" shows, from the variants list (each one's
      words worked out by snippets/stock-promise.liquid). No variant (an option mix that doesn't exist): neither. */
  const syncPromise = (section, variant) => {
    const box = section?.querySelector('[data-promise]');
    if (!box) return;
    const stock = variant && typeof variant.stock === 'string' ? variant.stock : '';
    const ships = Boolean(variant && variant.ships);
    const stockLine = box.querySelector('[data-promise-stock]');
    const text = box.querySelector('[data-promise-stock-text]');
    if (text) text.textContent = stock;
    if (stockLine) stockLine.hidden = !stock;
    const shipLine = box.querySelector('[data-promise-ship]');
    if (shipLine) shipLine.hidden = !ships;
    box.hidden = !stock && !(ships && shipLine);
  };

  /* ---------- Order as a gift (round 16, sections/main-product.liquid) ----------
     Mo (16 Oct 2026): "Order as gift will send a message and no price on the item or packing slip." Opening it switches
     its fields on, so the line goes into the cart with the properties Gift, Gift for, Gift from and Gift message (staff
     see them on the order and on the packing slip, which never shows prices), and Add to cart says Add gift to cart.
     Closed, its fields are switched off and none of them is sent. A field left empty isn't sent either. Once the gift is
     in the cart it closes and empties, so the next one isn't a gift by mistake. Hidden without JavaScript. */
  class GiftOption extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('[data-gift-details]');
      this.form = this.closest('form');
      if (!this.details || !this.form || this.ready) return;
      this.ready = true;
      this.hidden = false;
      this.details.addEventListener('toggle', () => this.sync());
      // capturing on the form's product-form runs before its own submit handler reads the form
      const holder = this.closest('product-form') || this.form;
      holder.addEventListener('submit', (event) => this.leaveOutEmpty(event), true);
      holder.addEventListener('product-form:added', () => this.reset());
      // Enter in a name field goes on to the next field, rather than adding a half-written gift to the cart
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' || event.isComposing || !event.target.matches('input[data-gift-field]')) return;
        event.preventDefault();
        const fields = Array.from(this.querySelectorAll('[data-gift-field]'));
        fields[fields.indexOf(event.target) + 1]?.focus();
      });
      this.sync();
    }

    get on() {
      return Boolean(this.details && this.details.open);
    }

    sync() {
      if (!this.details) return;
      const add = this.form.querySelector('[data-add-button]');
      // sold out (or an option mix that doesn't exist): nothing to send as a gift
      const off = Boolean(add && add.disabled);
      if (off && this.on) this.details.open = false;
      this.hidden = off;
      for (const field of this.querySelectorAll('[data-gift-field], [data-gift-flag]')) field.disabled = !this.on;
      const label = this.form.querySelector('[data-add-label]');
      if (add && label && !add.disabled) label.textContent = this.on ? this.dataset.labelGift : this.dataset.labelAdd;
      // Buy it now and the express checkouts go straight to checkout without the gift's details: away while it's open
      const pay = this.form.querySelector('.shopify-payment-button');
      if (pay) pay.style.display = this.on ? 'none' : '';
      syncBuyBar(this.closest('.product'));
    }

    leaveOutEmpty(event) {
      if (event.target !== this.form || !this.on) return;
      const left = [];
      for (const field of this.querySelectorAll('[data-gift-field]')) {
        if (field.disabled || field.value.trim()) continue;
        field.disabled = true;
        left.push(field);
      }
      // after the form's been read (the add, or the browser's own submit)
      if (left.length) window.setTimeout(() => left.forEach((field) => { field.disabled = !this.on; }), 0);
    }

    reset() {
      if (!this.on) return;
      for (const field of this.querySelectorAll('[data-gift-field]')) field.value = '';
      this.details.open = false;
    }
  }

  /* ---------- variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      try {
        this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      } catch {
        this.variants = [];
      }
      this.addEventListener('change', () => this.update());
      this.syncSelects(this.selected());
      // the browser put a different choice back (the back button): bring the price, button and picture with it
      const current = this.closest('form')?.querySelector('[data-variant-id]');
      const variant = this.variants.find((v) => v.options.every((opt, i) => opt === this.selected()[i]));
      if (variant && current && String(variant.id) !== current.value) this.update();
    }

    /** The chosen value of each option, in order: a checked button, or a dropdown's hidden value (round 9) */
    selected() {
      return Array.from(this.querySelectorAll('fieldset')).map((fs) => (fs.querySelector('input:checked') || fs.querySelector('[data-vselect-value]'))?.value);
    }

    /** Each dropdown's "Sold out" marks, for the values that can't be bought with the other options as chosen */
    syncSelects(selected) {
      for (const select of this.querySelectorAll('variant-select')) {
        const index = Number(select.dataset.optionIndex);
        for (const option of select.querySelectorAll('[role="option"]')) {
          const value = option.dataset.value;
          const ok = this.variants.some((v) => v.available && v.options[index] === value && v.options.every((opt, j) => j === index || opt === selected[j]));
          const sold = option.querySelector('[data-vselect-sold]');
          if (sold) sold.hidden = ok;
        }
        select.syncFlag?.();
      }
    }

    update() {
      const selected = this.selected();
      this.syncSelects(selected);
      const variant = this.variants.find((v) => v.options.every((opt, i) => opt === selected[i]));
      const section = this.closest('.product');
      const form = this.closest('form');
      const idInput = form?.querySelector('[data-variant-id]');
      const button = form?.querySelector('[data-add-button]');
      const label = form?.querySelector('[data-add-label]');
      if (!variant) {
        if (button) button.disabled = true;
        if (label) label.textContent = strings.unavailable || 'Unavailable';
        syncPromise(section, null);
        section?.querySelector('gift-option')?.sync?.();
        syncBuyBar(section);
        return;
      }
      if (idInput) idInput.value = variant.id;
      if (button) button.disabled = !variant.available;
      if (label) label.textContent = variant.available ? strings.addToCart || 'Add to cart' : strings.soldOut || 'Sold out';
      const plans = variant.plans || {};
      for (const input of form?.querySelectorAll('[data-plan-picker] input[name="selling_plan"]') || []) {
        const planPrice = input.value ? plans[input.value] : variant.price;
        if (!planPrice) continue;
        input.dataset.price = planPrice;
        const amount = input.closest('.plan-option')?.querySelector('[data-plan-amount]');
        if (amount) amount.textContent = planPrice;
      }
      const chosenPlan = form?.querySelector('[data-plan-picker] input[name="selling_plan"]:checked');
      const price = section?.querySelector('[data-product-price] .price');
      if (chosenPlan && chosenPlan.value) {
        showPlanPrice(chosenPlan);
      } else if (price) {
        price.classList.toggle('price--sale', Boolean(variant.compare));
        price.innerHTML = `<span class="price__current">${variant.price}</span>${variant.compare ? `<s class="price__compare">${variant.compare}</s>` : ''}`;
      }
      syncPromise(section, variant);
      section?.querySelector('gift-option')?.sync?.();
      syncBuyBar(section);
      // its picture, straight away and without a word (round 9: the gallery jumps, it doesn't scroll past the rest)
      if (variant.media) showSlide(section?.querySelector(`[data-media-id="${variant.media}"]`), true);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  }

  /* ---------- a long option as a dropdown with pictures (snippets/variant-select.liquid, round 9) ----------
     A button opens a panel with a filter field and the values (an ARIA combobox with a listbox popup: the filter is
     the combobox and points at the option the keys are on with aria-activedescendant). Arrows, Home and End, Page Up
     and Page Down move; typing filters, and typing on the closed button opens it and starts the filter; Enter picks;
     Escape closes; Tab closes and moves on. Picking puts the value in the hidden input and fires its change event,
     which variant-picker handles exactly as it does the option buttons. On a phone the panel is a sheet over the
     dimmed page, and the sheet takes the focus, so the keyboard only comes up when the filter is tapped. */
  const sheetWidth = window.matchMedia('(max-width: 749px)');
  class VariantSelect extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-vselect-button]');
      this.panel = this.querySelector('[data-vselect-panel]');
      this.filter = this.querySelector('[data-vselect-filter]');
      this.list = this.querySelector('[data-vselect-list]');
      this.input = this.querySelector('[data-vselect-value]');
      if (!this.button || !this.panel || !this.list || !this.input || this.ready) return;
      this.ready = true;
      this.options = Array.from(this.list.querySelectorAll('[role="option"]'));
      this.active = null;
      this.button.addEventListener('click', () => (this.isOpen ? this.close(true) : this.open()));
      this.button.addEventListener('keydown', (event) => this.onButtonKey(event));
      this.panel.addEventListener('keydown', (event) => this.onPanelKey(event));
      this.filter?.addEventListener('input', () => this.applyFilter());
      this.filter?.addEventListener('change', (event) => event.stopPropagation()); // typing isn't a new variant
      this.list.addEventListener('click', (event) => {
        const option = event.target.closest('[role="option"]');
        if (option) this.choose(option);
      });
      this.list.addEventListener('pointermove', (event) => {
        const option = event.target.closest('[role="option"]');
        this.panel.removeAttribute('data-keys');
        if (option && option !== this.active) this.setActive(option, false);
      });
      this.querySelector('[data-vselect-close]')?.addEventListener('click', () => this.close(true));
      this.onOutside = (event) => {
        if (this.panel.contains(event.target) || this.button.contains(event.target)) return;
        this.close(false);
      };
      // a value the browser put back (the back button) wins over the one the page was drawn with
      const chosen = this.options.find((o) => o.dataset.value === this.input.value);
      if (chosen && chosen.getAttribute('aria-selected') !== 'true') {
        for (const o of this.options) o.setAttribute('aria-selected', String(o === chosen));
        this.showChosen(chosen);
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    disconnectedCallback() {
      document.removeEventListener('pointerdown', this.onOutside, true);
      if (this.asSheet) document.body.classList.remove('has-open-sheet');
    }

    get isOpen() {
      return Boolean(this.panel) && !this.panel.hidden;
    }

    visible() {
      return this.options.filter((o) => !o.hidden);
    }

    chosen() {
      return this.options.find((o) => o.getAttribute('aria-selected') === 'true');
    }

    open(start) {
      if (this.isOpen) return;
      this.panel.hidden = false;
      this.toggleAttribute('data-open', true);
      this.button.setAttribute('aria-expanded', 'true');
      document.addEventListener('pointerdown', this.onOutside, true);
      if (this.filter) this.filter.value = '';
      this.applyFilter(true);
      const list = this.visible();
      const chosen = this.chosen();
      const target = start === 'first' ? list[0] : start === 'last' ? list[list.length - 1] : chosen && !chosen.hidden ? chosen : list[0];
      this.asSheet = sheetWidth.matches;
      if (this.asSheet) {
        document.body.classList.add('has-open-sheet');
        this.panel.removeAttribute('data-up');
        this.panel.style.maxHeight = '';
      } else {
        this.fitPanel();
      }
      (this.asSheet || !this.filter ? this.panel : this.filter).focus({ preventScroll: true });
      this.setActive(target, true, 'center');
    }

    /** A dropdown stays on screen: as tall as the room under the button, or opening upwards when there's more room
        above it (the product's buy column sticks, so the page can't scroll to a panel hanging off the bottom) */
    fitPanel() {
      const box = this.button.getBoundingClientRect();
      const top = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-top')) || 0;
      const below = window.innerHeight - box.bottom - 16;
      const above = box.top - top - 16;
      const up = below < 260 && above > below;
      this.panel.toggleAttribute('data-up', up);
      this.panel.style.maxHeight = `${Math.round(Math.max(200, Math.min(448, up ? above : below)))}px`;
    }

    close(returnFocus) {
      if (!this.isOpen) return;
      this.panel.hidden = true;
      this.removeAttribute('data-open');
      this.button.setAttribute('aria-expanded', 'false');
      this.panel.removeAttribute('data-keys');
      this.setActive(null);
      const status = this.querySelector('[data-vselect-status]');
      if (status) status.textContent = '';
      document.removeEventListener('pointerdown', this.onOutside, true);
      if (this.asSheet) document.body.classList.remove('has-open-sheet');
      this.asSheet = false;
      if (returnFocus) this.button.focus({ preventScroll: true });
    }

    setActive(option, scroll = true, align = 'nearest') {
      this.active?.removeAttribute('data-active');
      this.active = option || null;
      if (!option) {
        this.filter?.removeAttribute('aria-activedescendant');
        return;
      }
      option.setAttribute('data-active', '');
      this.filter?.setAttribute('aria-activedescendant', option.id);
      if (scroll) this.reveal(option, align);
    }

    /** Scroll the list, and only the list (never the page), so the option shows */
    reveal(option, align) {
      const list = this.list;
      const top = option.getBoundingClientRect().top - list.getBoundingClientRect().top + list.scrollTop;
      const height = option.offsetHeight;
      if (align === 'center') list.scrollTop = top - (list.clientHeight - height) / 2;
      else if (top < list.scrollTop) list.scrollTop = top - 6;
      else if (top + height > list.scrollTop + list.clientHeight) list.scrollTop = top + height - list.clientHeight + 6;
    }

    move(step) {
      const list = this.visible();
      if (!list.length) return;
      const at = list.indexOf(this.active);
      const next = at < 0 ? (step > 0 ? 0 : list.length - 1) : Math.max(0, Math.min(list.length - 1, at + step));
      this.setActive(list[next]);
    }

    /** Show the values with a word starting with each word typed ("red" finds Bloody Red, not Charred Brown; "pokemon"
        finds Pokémon), and put the keys on the first whose name starts with it */
    applyFilter(quiet = false) {
      const typed = (this.filter?.value || '').trim();
      const plain = (text) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      const query = plain(typed);
      const words = query.split(/\s+/).filter(Boolean);
      let first = null;
      let starts = null;
      let count = 0;
      for (const option of this.options) {
        const name = plain(option.dataset.value || '');
        const parts = name.split(/[\s\-/(),.]+/).filter(Boolean);
        const show = !query || words.every((word) => parts.some((part) => part.startsWith(word)) || name.startsWith(word));
        option.hidden = !show;
        if (!show) continue;
        count += 1;
        first = first || option;
        if (!starts && query && name.startsWith(query)) starts = option;
      }
      const empty = this.querySelector('[data-vselect-empty]');
      if (empty) {
        empty.hidden = count > 0;
        empty.textContent = count ? '' : fill(this.dataset.none || 'Nothing matches [query].', { query: typed });
      }
      if (quiet) return;
      const status = this.querySelector('[data-vselect-status]');
      if (status) status.textContent = !query ? '' : count ? fill(count === 1 ? this.dataset.countOne || '[count] match' : this.dataset.countOther || '[count] matches', { count }) : empty?.textContent || '';
      const chosen = this.chosen();
      this.setActive(query ? starts || first : chosen && !chosen.hidden ? chosen : first, true, query ? 'nearest' : 'center');
      if (query) this.list.scrollTop = 0;
      if (this.active) this.reveal(this.active, 'nearest');
    }

    choose(option) {
      const value = option.dataset.value;
      for (const o of this.options) o.setAttribute('aria-selected', String(o === option));
      const changed = this.input.value !== value;
      this.input.value = value;
      this.showChosen(option);
      this.close(true);
      if (changed) this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /** The button shows the chosen value: its picture, its name, and Sold out when it is */
    showChosen(option) {
      const name = this.querySelector('[data-vselect-name]');
      if (name) name.textContent = option.dataset.value;
      const thumb = this.querySelector('[data-vselect-thumb]');
      const img = option.querySelector('img');
      if (thumb) {
        const copy = img ? img.cloneNode() : null;
        if (copy) copy.loading = 'eager';
        thumb.replaceChildren(...(copy ? [copy] : []));
      }
      this.syncFlag(option);
    }

    syncFlag(option = this.chosen()) {
      const flag = this.querySelector('[data-vselect-flag]');
      const sold = option?.querySelector('[data-vselect-sold]');
      if (flag) flag.hidden = !sold || sold.hidden;
    }

    onButtonKey(event) {
      const { key } = event;
      if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Home' || key === 'End') {
        event.preventDefault();
        this.open(key === 'Home' ? 'first' : key === 'End' ? 'last' : undefined);
        this.panel.toggleAttribute('data-keys', true);
      } else if (key.length === 1 && key !== ' ' && !event.ctrlKey && !event.metaKey && !event.altKey) {
        // type-ahead: open it and start the filter with that letter
        event.preventDefault();
        this.open();
        this.panel.toggleAttribute('data-keys', true);
        this.typeInto(key);
      }
    }

    typeInto(text) {
      if (!this.filter) return;
      this.filter.focus({ preventScroll: true });
      this.filter.value += text;
      this.applyFilter();
    }

    onPanelKey(event) {
      const { key } = event;
      const steps = { ArrowDown: 1, ArrowUp: -1, PageDown: 8, PageUp: -8 };
      if (key in steps) {
        event.preventDefault();
        this.panel.toggleAttribute('data-keys', true);
        this.move(steps[key]);
      } else if (key === 'Home' || key === 'End') {
        event.preventDefault();
        this.panel.toggleAttribute('data-keys', true);
        const list = this.visible();
        this.setActive(key === 'Home' ? list[0] : list[list.length - 1]);
      } else if (key === 'Enter') {
        event.preventDefault(); // never adds to the cart from here
        if (this.active && !this.active.hidden) this.choose(this.active);
      } else if (key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.close(true);
      } else if (key === 'Tab') {
        this.close(true);
      } else if (event.target === this.panel && key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        // typing on the sheet itself goes into the filter
        event.preventDefault();
        this.typeInto(key);
      }
    }
  }

  /* ---------- selling plans (subscriptions) ---------- */
  const showPlanPrice = (input) => {
    const price = input.closest('.product')?.querySelector('[data-product-price] .price');
    if (!price) return;
    price.classList.remove('price--sale');
    price.replaceChildren();
    const current = document.createElement('span');
    current.className = 'price__current';
    current.textContent = input.dataset.price || '';
    price.append(current);
    if (input.dataset.interval) {
      const per = document.createElement('span');
      per.className = 'price__per';
      per.textContent = input.dataset.interval;
      price.append(per);
    }
    syncBuyBar(input.closest('.product'));
  };
  document.addEventListener('change', (event) => {
    const input = event.target.closest?.('[data-plan-picker] input[name="selling_plan"]');
    if (!input) return;
    showPlanPrice(input);
    const url = new URL(window.location.href);
    if (input.value) url.searchParams.set('selling_plan', input.value);
    else url.searchParams.delete('selling_plan');
    window.history.replaceState({}, '', url);
  });

  /* ---------- chip rows: fade the edges that have more chips, keep the active chip in view ---------- */
  for (const row of document.querySelectorAll('[data-chip-row]')) {
    const sync = () => {
      const max = row.scrollWidth - row.clientWidth;
      row.toggleAttribute('data-scrolled', row.scrollLeft > 4);
      row.toggleAttribute('data-end', row.scrollLeft >= max - 4);
    };
    const active = row.querySelector('[aria-current="true"]');
    if (active && row.scrollWidth > row.clientWidth) {
      const item = active.closest('li') || active;
      row.scrollLeft = Math.max(0, item.offsetLeft - (row.clientWidth - item.offsetWidth) / 2);
    }
    row.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync, { passive: true });
    sync();
  }

  /* ---------- sort menus that submit themselves ---------- */
  document.addEventListener('change', (event) => {
    const form = event.target.closest?.('form[data-autosubmit]');
    if (!form) return;
    if (form.requestSubmit) form.requestSubmit();
    else form.submit();
  });

  /* ---------- "On this page" lists stay open beside the text on wide screens ---------- */
  const wide = window.matchMedia('(min-width: 990px)');
  const syncToc = () => {
    for (const toc of document.querySelectorAll('[data-toc]')) {
      if (wide.matches) toc.open = true;
    }
  };
  syncToc();
  wide.addEventListener?.('change', syncToc);

  /* ---------- footer menus: folded accordions on phones, open columns from 750px (sections/site-footer.liquid) ---------- */
  const footerWide = window.matchMedia('(min-width: 750px)');
  const syncFooterMenus = () => {
    for (const menu of document.querySelectorAll('[data-footer-menu]')) menu.open = footerWide.matches;
  };
  syncFooterMenus();
  footerWide.addEventListener?.('change', syncFooterMenus);
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-toc] a[href^="#"]');
    if (link && !wide.matches) link.closest('[data-toc]').open = false;
  });

  /* ---------- product gallery thumbnails ---------- */
  /** Slide the gallery to one picture. Only the gallery moves: scrollIntoView would scroll the page too, pulling a
      phone back up to the pictures while someone picks a variant further down. */
  function showSlide(slide, instant = false) {
    const track = slide?.closest('[data-gallery-track]');
    if (!track || !track.firstElementChild) return;
    track.scrollTo({ left: slide.offsetLeft - track.firstElementChild.offsetLeft, behavior: instant || reduceMotion.matches ? 'auto' : 'smooth' });
    // its thumbnail is the current one, scrolled into view along its own row (round 9: a product can have hundreds)
    const row = track.closest('[data-gallery]')?.querySelector('.product__thumbs');
    if (!row) return;
    let current = null;
    for (const thumb of row.querySelectorAll('[data-thumb]')) {
      const on = thumb.dataset.thumb === slide.id;
      thumb.setAttribute('aria-current', String(on));
      if (on) current = thumb;
    }
    if (current && row.scrollWidth > row.clientWidth) {
      const offset = current.getBoundingClientRect().left - row.getBoundingClientRect().left;
      row.scrollLeft += offset - (row.clientWidth - current.offsetWidth) / 2;
    }
  }
  document.addEventListener('click', (event) => {
    const thumb = event.target.closest('[data-thumb]');
    if (!thumb) return;
    showSlide(document.getElementById(thumb.dataset.thumb));
    for (const t of thumb.closest('ul').querySelectorAll('[data-thumb]')) t.setAttribute('aria-current', String(t === thumb));
  });

  /* ---------- rails ---------- */
  for (const controls of document.querySelectorAll('[data-rail-controls]')) {
    const rail = document.getElementById(controls.dataset.railControls);
    if (!rail) continue;
    const prev = controls.querySelector('[data-rail-prev]');
    const next = controls.querySelector('[data-rail-next]');
    const sync = () => {
      prev.disabled = rail.scrollLeft <= 4;
      next.disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 4;
    };
    prev.addEventListener('click', () => rail.scrollBy({ left: -rail.clientWidth * 0.9, behavior: reduceMotion.matches ? 'auto' : 'smooth' }));
    next.addEventListener('click', () => rail.scrollBy({ left: rail.clientWidth * 0.9, behavior: reduceMotion.matches ? 'auto' : 'smooth' }));
    rail.addEventListener('scroll', sync, { passive: true });
    sync();
  }

  /* ---------- the ad link someone came by (round 24, Lair contract v24-new-players) ----------
     Dice Goblin's ads are judged by the new players they bring in, so the first ad link this browser arrives by is
     kept for 30 days: its utm_source, utm_medium and utm_campaign (Google's gclid, gbraid or wbraid alone is
     google/cpc). lair-core.js sends them with a booking or sign-up, and once to My Lair's account when someone's
     logged in. Only the link's tags are kept, nothing about who, and a browser that blocks storage keeps nothing. */
  (() => {
    const AD_KEY = 'lair-ad';
    const AD_DAYS = 30;
    try {
      const params = new URLSearchParams(window.location.search);
      const tidy = (value, max) => String(value || '').toLowerCase().trim().replace(/[^a-z0-9._+-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, max);
      let source = tidy(params.get('utm_source'), 40);
      let medium = tidy(params.get('utm_medium'), 40);
      if (!source && (params.get('gclid') || params.get('gbraid') || params.get('wbraid'))) {
        source = 'google';
        medium = medium || 'cpc';
      }
      if (!source) return;
      const raw = window.localStorage.getItem(AD_KEY);
      let kept = null;
      try {
        kept = raw ? JSON.parse(raw) : null;
      } catch {
        kept = null; // a bad entry is replaced
      }
      if (kept && kept.source && Date.now() - Number(kept.at) < AD_DAYS * 24 * 3600 * 1000) return;
      window.localStorage.setItem(AD_KEY, JSON.stringify({ source, medium: medium || null, campaign: tidy(params.get('utm_campaign'), 80) || null, at: Date.now() }));
    } catch {
      /* storage blocked: nothing kept */
    }
  })();

  /* ---------- shared with section scripts (the d20 roller in d20-roller.js) ---------- */
  window.DiceGoblin = Object.freeze({
    announce,
    diceBurst,
    cartRequest,
    applySections,
    /** Open the cart drawer, or go to the cart page when the theme uses one */
    openCart(opener) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && config.cartType === 'drawer') drawer.open(opener);
      else window.location.href = routes.cart || '/cart';
    },
  });

  /* ---------- predictive search ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-search-results]');
      this.controller = null;
      this.input.addEventListener('input', () => {
        window.clearTimeout(this.timer);
        this.timer = window.setTimeout(() => this.search(), 220);
      });
      this.input.addEventListener('keydown', (event) => this.keys(event));
      // Emptied once the search has finished closing (base.css fades it out), so it doesn't shrink as it goes
      const dialog = this.closest('dialog');
      dialog?.addEventListener('close', () => {
        window.setTimeout(() => {
          if (dialog.open) return;
          this.input.value = '';
          this.results.innerHTML = '';
        }, 250);
      });
    }

    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) {
        this.results.innerHTML = '';
        this.input.setAttribute('aria-expanded', 'false');
        return;
      }
      this.controller?.abort();
      this.controller = new AbortController();
      const url = new URL(`${routes.predictiveSearch || '/search/suggest'}.json`, window.location.origin);
      url.searchParams.set('q', q);
      url.searchParams.set('resources[type]', 'product,collection,page');
      url.searchParams.set('resources[limit]', '6');
      // The shop shows only what can be bought (round 7): Shopify leaves sold-out products out
      url.searchParams.set('resources[options][unavailable_products]', 'hide');
      try {
        const response = await fetch(url, { signal: this.controller.signal });
        const data = await response.json();
        this.render(q, data.resources?.results || {});
      } catch (error) {
        if (error.name !== 'AbortError') this.results.innerHTML = '';
      }
    }

    render(q, results) {
      const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
      const collections = results.collections || [];
      const pages = results.pages || [];
      let html = '';
      // The suggest API has no metafields, so library copies (shelf code in custom.library_code
      // in Liquid) are spotted by the tag every one of them carries, "Board Game Rental", with
      // "(Library)" in the title as a backup. Not by a $0 price: a few shop products are free.
      // They show "Library" instead of a price.
      const isLibrary = (p) => (p.tags || []).includes('Board Game Rental') || /\(library\)\s*$/i.test(p.title || '');
      // Anything sold out that still comes through stays out; library copies always show
      const products = (results.products || []).filter((p) => p.available !== false || isLibrary(p));
      if (products.length) {
        html += `<div class="predictive__group"><p class="predictive__heading">${esc(strings.searchProducts || 'Products')}</p><ul class="predictive__list" role="listbox">${products
          .map((p, i) => {
            const library = isLibrary(p);
            const title = library ? String(p.title || '').replace(/\s*\(library\)\s*$/i, '') : p.title;
            const tail = library
              ? `<span class="badge badge--library">${esc(strings.searchLibrary || 'Library')}</span>`
              : `<span class="price">${esc(p.price ? `$${Number(p.price).toFixed(2)}` : '')}</span>`;
            return `<li><a class="predictive__item" id="ps-${i}" role="option" aria-selected="false" href="${esc(p.url)}">${p.image ? `<img class="predictive__thumb" src="${esc(p.image)}&width=96" alt="" width="48" height="48" loading="lazy">` : '<span class="predictive__thumb"></span>'}<span class="predictive__title">${esc(title)}</span>${tail}</a></li>`;
          })
          .join('')}</ul></div>`;
      }
      if (collections.length || pages.length) {
        html += `<div class="predictive__group"><p class="predictive__heading">${esc(strings.searchCollections || 'Collections and pages')}</p><ul class="predictive__list">${[...collections, ...pages]
          .map((c) => `<li><a class="predictive__item" href="${esc(c.url)}" style="grid-template-columns:1fr"><span class="predictive__title">${esc(c.title)}</span></a></li>`)
          .join('')}</ul></div>`;
      }
      // Nothing found: Gobgob in person, beside the words (round 7)
      const gob = config.gobgob ? `<img class="gobgob" src="${esc(config.gobgob)}" width="240" height="224" alt="" loading="lazy" decoding="async">` : '';
      if (!html) html = `<div class="gob-moment predictive__empty">${gob}<p class="muted">${esc(fill(strings.searchNoResults || 'Nothing found for [terms]', { terms: q }))}</p></div>`;
      html += `<a class="text-link" href="${esc(routes.search || '/search')}?q=${encodeURIComponent(q)}&options%5Bprefix%5D=last&options%5Bunavailable_products%5D=hide">${esc(fill(strings.searchAll || 'See all results for [terms]', { terms: q }))}</a>`;
      this.results.innerHTML = html;
      this.input.setAttribute('aria-expanded', 'true');
      this.active = -1;
    }

    keys(event) {
      const items = Array.from(this.results.querySelectorAll('[role="option"]'));
      if (!items.length || !['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) return;
      if (event.key === 'Enter') {
        if (this.active >= 0) {
          event.preventDefault();
          items[this.active].click();
        }
        return;
      }
      event.preventDefault();
      this.active = (this.active + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
      items.forEach((item, i) => item.setAttribute('aria-selected', String(i === this.active)));
      this.input.setAttribute('aria-activedescendant', items[this.active].id);
    }
  }

  /* ---------- filters ---------- */
  class FacetsPanel extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.form = this.querySelector('[data-facets-form]');
      this.section = this.closest('[data-facets-section]');
      this.opener = this.section?.querySelector('[data-facets-open]');
      this.opener?.addEventListener('click', () => this.toggle(true));
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-facets-close]')) this.toggle(false);
      });
      this.addEventListener('change', () => {
        window.clearTimeout(this.timer);
        this.timer = window.setTimeout(() => this.apply(), 350);
      });
      this.form?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.apply();
        this.toggle(false);
      });
      window.addEventListener('popstate', () => this.load(window.location.search, false));
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.classList.contains('is-open')) this.toggle(false);
      });
    }

    toggle(open) {
      this.classList.toggle('is-open', open);
      this.opener?.setAttribute('aria-expanded', String(open));
      if (open) this.querySelector('select, input')?.focus();
      else this.opener?.focus();
    }

    apply() {
      const params = new URLSearchParams(new FormData(this.form));
      for (const [key, value] of Array.from(params.entries())) if (value === '') params.delete(key);
      this.load(`?${params.toString()}`, true);
    }

    async load(search, push) {
      const main = this.section?.querySelector('[data-results]');
      main?.setAttribute('aria-busy', 'true');
      try {
        const url = `${window.location.pathname}${search}${search.includes('?') ? '&' : '?'}section_id=${encodeURIComponent(this.sectionId)}`;
        const html = await (await fetch(url)).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const freshMain = doc.querySelector('[data-results]');
        const freshForm = doc.querySelector('[data-facets-form]');
        const focusName = document.activeElement?.name;
        const focusValue = document.activeElement?.value;
        if (freshMain && main) main.replaceWith(freshMain);
        if (freshForm && this.form) {
          this.form.replaceWith(freshForm);
          this.form = freshForm;
          this.form.addEventListener('submit', (event) => {
            event.preventDefault();
            this.apply();
            this.toggle(false);
          });
          if (focusName) {
            const again = Array.from(this.form.elements).find((el) => el.name === focusName && (el.type !== 'checkbox' || el.value === focusValue));
            again?.focus();
          }
        }
        this.opener = this.section?.querySelector('[data-facets-open]');
        this.opener?.addEventListener('click', () => this.toggle(true));
        if (push) window.history.pushState({}, '', `${window.location.pathname}${search}`);
      } catch {
        window.location.search = search;
      } finally {
        this.section?.querySelector('[data-results]')?.removeAttribute('aria-busy');
      }
    }
  }

  /* ---------- product recommendations ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      const observer = new IntersectionObserver(
        async (entries) => {
          if (!entries[0].isIntersecting) return;
          observer.disconnect();
          try {
            const html = await (await fetch(this.dataset.url)).text();
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const fresh = doc.querySelector('product-recommendations');
            if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
          } catch {
            /* recommendations are optional */
          }
        },
        { rootMargin: '0px 0px 400px 0px' },
      );
      observer.observe(this);
    }
  }

  customElements.define('cart-drawer', CartDrawer);
  customElements.define('product-form', ProductForm);
  customElements.define('buy-bar', BuyBar);
  customElements.define('gift-option', GiftOption);
  customElements.define('variant-select', VariantSelect);
  customElements.define('variant-picker', VariantPicker);
  customElements.define('predictive-search', PredictiveSearch);
  customElements.define('facets-panel', FacetsPanel);
  customElements.define('product-recommendations', ProductRecommendations);

  document.querySelector('[data-focus-on-load]')?.focus();
  // Safari on iPhone only shows :active (the press feedback on buttons) when the page listens for touches
  document.body.addEventListener('touchstart', () => {}, { passive: true });
})();
